import React, { useState } from 'react';
import { ListChecks, Pill } from 'lucide-react';
import { cn } from '@/lib/utils';
import PatientPrescriptionsTab from './PatientPrescriptionsTab';
import PatientMedicationsTab from './PatientMedicationsTab';

interface PatientOrdersRxTabProps {
  patientId: string;
}

type OrdersRxView = 'orders' | 'medications';

/**
 * Combined Orders & Rx tab. Replaces the legacy 3-tab confusion (Orders,
 * Prescriptions, Medications) with a single tab and an internal toggle:
 *  - Orders view: header-level Rx orders (payment, fulfillment, cancel/refill)
 *  - Medications view: flattened medication line items across all Rx
 * The FHIR Medication List moved to the Chart tab.
 */
export default function PatientOrdersRxTab({
  patientId,
}: PatientOrdersRxTabProps) {
  const [view, setView] = useState<OrdersRxView>('orders');

  return (
    <div className='space-y-3'>
      <div className='flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1'>
        <ToggleButton active={view === 'orders'} onClick={() => setView('orders')}>
          <ListChecks className='h-3.5 w-3.5' />
          Rx Orders
        </ToggleButton>
        <ToggleButton
          active={view === 'medications'}
          onClick={() => setView('medications')}
        >
          <Pill className='h-3.5 w-3.5' />
          Active Medications
        </ToggleButton>
      </div>

      {view === 'orders' ? (
        <PatientPrescriptionsTab patientId={patientId} />
      ) : (
        <PatientMedicationsTab patientId={patientId} />
      )}
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-teal-50 text-teal-700'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}
